import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";

import TextBox from "../TextBox";
import Button from "../Button";
import ButtonLink from "../ButtonLink";
import useCommission from "../../../hooks/useCommissions";
import useQuotation from "../../../hooks/useQuotation";
import useChat from "../../../hooks/useChat";
import useInvoice from "../../../hooks/useInvoice";
import MessageContainer from "./MessageContainer";
import ChatSessionContext from "../../../contexts/ChatSessionContext";

import { ReactComponent as TrueMoneyIdleSvg } from "../../../assets/payment/truemoney-idle.svg";
import { ReactComponent as TrueMoneyActiveSvg } from "../../../assets/payment/truemoney-active.svg";
import { ReactComponent as CreditCardIdleSvg } from "../../../assets/payment/credit-card-idle.svg";
import { ReactComponent as CreditCardActiveSvg } from "../../../assets/payment/credit-card-active.svg";
import { ReactComponent as QRIdleSvg } from "../../../assets/payment/qrcode-idle.svg";
import { ReactComponent as QRActiveSvg } from "../../../assets/payment/qrcode-active.svg";
import { ReactComponent as VisaSvg } from "../../../assets/payment/visa.svg";
import { ReactComponent as MasterCardSvg } from "../../../assets/payment/mastercard.svg";
import { ReactComponent as UnionPaySvg } from "../../../assets/payment/unionpay.svg";
import { ReactComponent as JcbSvg } from "../../../assets/payment/jcb.svg";
import { ReactComponent as PromptPayLogoSvg } from "../../../assets/payment/prompt-pay-logo.svg";
import paymentQRImage from "../../../assets/temp/payment-qr.png";
import paymentTrueMoneyImage from "../../../assets/temp/payment-truemoney.png";
import usePayments from "../../../hooks/usePayments";
import { langComponent } from "../../../constants";
import AppContext from "../../../AppContext";

const TAB = {
  TRUEMONEY: 1,
  CREDIT_CARD: 2,
  QRCODE: 3,
};

const tabs = [
  {
    icon: <TrueMoneyIdleSvg />,
    iconActive: <TrueMoneyActiveSvg />,
    value: TAB.TRUEMONEY,
    name: "TrueMoney",
  },
  {
    icon: <CreditCardIdleSvg />,
    iconActive: <CreditCardActiveSvg />,
    value: TAB.CREDIT_CARD,
    name: "Credit Card",
  },
  {
    icon: <QRIdleSvg />,
    iconActive: <QRActiveSvg />,
    value: TAB.QRCODE,
    name: "QR Code",
  },
];

const PaymentForm = ({ commission, onSuccess, onClose }) => {
  const { currentSession: session } = useContext(ChatSessionContext);

  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.chat);
  const TranslationCommon = getLang(langComponent.common);

  const [toggleDisplay, setToggleDisplay] = useState(true);
  const [descriptions, setDescriptions] = useState({
    name: "",
    desc: "",
    drafts: 1,
  });
  const [price, setPrice] = useState(undefined);
  const { createCommission, patchCommission } = useCommission();
  const { createQuotation } = useQuotation();
  const { createChat, CHAT_TYPE } = useChat();
  const { payInvoice } = useInvoice();
  const { createPayment, chargePayment } = usePayments();
  const [summary, setSummary] = useState(undefined);
  const [tab, setTab] = useState(TAB.CREDIT_CARD);

  useEffect(() => {
    // * Calculate prices
    const { price } = commission;

    let summary = {
      price,
      vat: (price * 7) / 100,
      charge: (price * 3) / 100,
    };

    setSummary(summary);

    return () => {};
  }, []);

  const handleSubmitDescription = (values, options) => {
    setDescriptions(values);
    setStep(2);
  };

  const handleSubmitCreditCard = async (values, options) => {
    const { name, cardNo, expiry, cvc } = values;

    const [expirationMonth, expirationYear] = expiry.split("/");

    const tokenParameters = {
      expiration_month: +expirationMonth,
      expiration_year: +expirationYear,
      name,
      number: cardNo,
      security_code: cvc,
    };

    Omise.createToken("card", tokenParameters, async (statusCode, response) => {
      if (!response || !response.card) {
        console.error("Error calling omise");
        return;
      }

      const cardToken = response.id;

      let { price, vat, charge } = summary;

      const grandTotal = price + vat + charge;

      console.log("grandTotal", grandTotal);

      const payment = await createPayment({
        commission: commission._id,
        total: price,
        vat,
        serviceCharge: charge,
        grandTotal,
      });

      // * Patch charge payment to commission
      await patchCommission(commission._id, {
        charge: payment.data._id,
      });

      // * Charge payment
      const chargeResponse = await chargePayment(payment.data._id, {
        card: cardToken,
        amount: grandTotal,
      });

      // * Mark paid invoice
      const payInvoiceResponse = await payInvoice(commission.invoice);

      // * Patch commission status
      await patchCommission(commission._id, {
        status: 2,
      });

      // TODO: Bind socket event to get result of charge

      setToggleDisplay(false);
      onSuccess();
    });
  };

  const handleClose = () => {
    setToggleDisplay(false);
    onClose();
  };

  if (!toggleDisplay) {
    return null;
  }

  return (
    <MessageContainer alignment={"center"}>
      <div className="chat-payment-form">
        <h3 className="chat-payment-form__header">{Translation("Payment")}</h3>
        <div className="chat-payment-form-tabs">
          {tabs.map((t) => {
            const active = tab === t.value;

            return (
              <div
                className={`chat-payment-form-tabs-item ${
                  active ? "chat-payment-form-tabs-item--active" : ""
                }`}
                onClick={() => setTab(t.value)}
              >
                {active ? t.iconActive : t.icon}
                <span>{Translation(t.name)}</span>
              </div>
            );
          })}
        </div>
        {tab === 1 && (
          <Formik initialValues={{}} onSubmit={handleSubmitCreditCard}>
            <Form className="chat-payment-form-content chat-payment-form-content-qr">
              <div className="chat-payment-form-content-qr-code">
                <img src={paymentTrueMoneyImage} alt="truemoney-qr" />
                <ButtonLink content={"Download"} />
              </div>
              <h3 className="chat-payment-form-content-qr__header">
                {Translation("Cost Summary")}
              </h3>
              <label className="chat-payment-form-content-qr-number__label">
                {Translation("Price")}
              </label>
              <label className="chat-payment-form-content-qr-number__value">
                {summary?.price.toLocaleString()}
              </label>
              <label className="chat-payment-form-content-qr-number__label">
                {Translation("Vat 7%")}
              </label>
              <label className="chat-payment-form-content-qr-number__value">
                {summary?.vat.toLocaleString()}
              </label>
              <label className="chat-payment-form-content-qr-number__label">
                {Translation("Grand Total")}
              </label>
              <label
                className="chat-payment-form-content-qr-number__value"
                style={{ fontSize: 18, fontWeight: 800 }}
              >
                {(summary?.price + summary?.vat).toLocaleString()}
              </label>
              <Button
                label={TranslationCommon("Cancel")}
                variant={"secondary"}
                onClick={handleClose}
              />
              <Button label={TranslationCommon("Continue")} type="submit" />
            </Form>
          </Formik>
        )}
        {tab === 2 && (
          <Formik
            initialValues={{ cardNo: "", expiry: "", cvc: "", name }}
            onSubmit={handleSubmitCreditCard}
          >
            <Form className="chat-payment-form-content chat-payment-form-content-creditcard">
              <label className="chat-payment-form-content-creditcard-number__textbox-label">
                {Translation("Credit Card Number")}
              </label>
              <div className="chat-payment-form-content-creditcard-number__type-icons">
                <JcbSvg />
                <UnionPaySvg />
                <MasterCardSvg />
                <VisaSvg />
              </div>
              <TextBox
                className="chat-payment-form-content-creditcard-number__field"
                name="cardNo"
                width="324"
              />
              <TextBox
                className="chat-payment-form-content__name"
                name="name"
                width="324"
                label={Translation("Name")}
              />
              <TextBox
                label={Translation("Expiry Date")}
                name="expiry"
                width="134"
              />
              <TextBox label={Translation("CVC")} name="cvc" width="134" />
              <h3 className="chat-payment-form-content-creditcard__header">
                {Translation("Cost Summary")}
              </h3>
              <label className="chat-payment-form-content-creditcard-number__label">
                {Translation("Price")}
              </label>
              <label className="chat-payment-form-content-creditcard-number__value">
                {summary?.price.toLocaleString()}
              </label>
              <label className="chat-payment-form-content-creditcard-number__label">
                {Translation("Charge 3%")}
              </label>
              <label className="chat-payment-form-content-creditcard-number__value">
                {summary?.charge.toLocaleString()}
              </label>
              <label className="chat-payment-form-content-creditcard-number__label">
                {Translation("Vat 7%")}
              </label>
              <label className="chat-payment-form-content-creditcard-number__value">
                {summary?.vat.toLocaleString()}
              </label>
              <label className="chat-payment-form-content-creditcard-number__label">
                {Translation("Grand Total")}
              </label>
              <label
                className="chat-payment-form-content-creditcard-number__value"
                style={{ fontSize: 18, fontWeight: 800 }}
              >
                {(
                  summary?.price +
                  summary?.charge +
                  summary?.vat
                ).toLocaleString()}
              </label>
              <Button
                label={TranslationCommon("Cancel")}
                variant={"secondary"}
                onClick={handleClose}
              />
              <Button label={TranslationCommon("Continue")} type="submit" />
            </Form>
          </Formik>
        )}
        {tab === 3 && (
          <Formik initialValues={{}} onSubmit={handleSubmitCreditCard}>
            <Form className="chat-payment-form-content chat-payment-form-content-qr">
              <div className="chat-payment-form-content-qr-code">
                <PromptPayLogoSvg />
                <img src={paymentQRImage} alt="promptpay-qr" />
                <ButtonLink content={"Download"} variant={"tietery"} />
              </div>
              <h3 className="chat-payment-form-content-qr__header">
                {Translation("Cost Summary")}
              </h3>
              <label className="chat-payment-form-content-qr-number__label">
                {Translation("Price")}
              </label>
              <label className="chat-payment-form-content-qr-number__value">
                {summary?.price.toLocaleString()}
              </label>
              <label className="chat-payment-form-content-qr-number__label">
                {Translation("Vat 7%")}
              </label>
              <label className="chat-payment-form-content-qr-number__value">
                {summary?.vat.toLocaleString()}
              </label>
              <label className="chat-payment-form-content-qr-number__label">
                {Translation("Grand Total")}
              </label>
              <label
                className="chat-payment-form-content-qr-number__value"
                style={{ fontSize: 18, fontWeight: 800 }}
              >
                {(summary?.price + summary?.vat).toLocaleString()}
              </label>
              <Button
                label={TranslationCommon("Cancel")}
                variant={"secondary"}
                onClick={handleClose}
              />
              <Button label={TranslationCommon("Continue")} type="submit" />
            </Form>
          </Formik>
        )}
      </div>
    </MessageContainer>
  );
};

PaymentForm.propTypes = {
  session: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

PaymentForm.defaultProps = {
  session: {},
  onClose: () => {},
  onSuccess: () => {},
};

export default PaymentForm;
